import React, { createContext, useContext, useEffect, useState } from 'react';
import { restorePortalAttempt } from '../utils/portalLogin.js';

const UnifiedAuthContext = createContext();

export const useAuth = () => {
    const context = useContext(UnifiedAuthContext);
    if (!context) {
        throw new Error('useAuth must be used within a UnifiedAuthProvider');
    }
    return context;
};

const apiRequest = async (url, options = {}) => {
    const response = await fetch(url, {
        credentials: 'include',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const requestError = new Error(
            errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
        requestError.status = response.status;
        throw requestError;
    }

    return response.json();
};

const getBrowserAuthParams = () => {
    const params = new URLSearchParams(window.location.search);

    // URL credentials are permanently retired. Remove a legacy token without
    // ever forwarding it to the API, while retaining visible routing context.
    if (params.has('token')) {
        params.delete('token');
        const query = params.toString();
        window.history.replaceState(
            window.history.state,
            '',
            `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
        );
    }

    return {
        location: params.get('location'),
        user: params.get('user'),
        integration: params.get('integration')
    };
};

const buildContextUrl = (path, { location, user, integration }) => {
    const params = new URLSearchParams();
    if (location) params.set('location', location);
    if (user) params.set('user', user);
    if (integration) params.set('integration', integration);

    const query = params.toString();
    return `${path}${query ? `?${query}` : ''}`;
};

export const UnifiedAuthProvider = ({ children }) => {
    const [technician, setTechnician] = useState(null);
    const [client, setClient] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [urlParams, setUrlParams] = useState({});

    useEffect(() => {
        initializeAuth();
    }, []);

    const initializeAuth = async () => {
        const browserParams = getBrowserAuthParams();
        const isPublicPage = window.location.pathname === '/login';
        const isResumingPortalLogin = window.self !== window.top
            && restorePortalAttempt({
                locationId: browserParams.location,
                userId: browserParams.user
            });

        setUrlParams(browserParams);

        // Only the Google sign-in entry page is public. Every page that reads or
        // changes tenant data, including integration configuration, must first
        // verify the HttpOnly session against the location and user in the URL.
        // A portal reload can still contain the pre-login user ID while Google
        // has just issued the canonical administrator session. Let Login finish
        // the saved flow's non-clearing verification before checking that stale
        // URL; ordinary mismatch rejection would erase the fresh cookie.
        if (isPublicPage || isResumingPortalLogin) {
            setTechnician(null);
            setClient(null);
            setUserRole(null);
            setError('');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError('');

            if (!browserParams.location || !browserParams.user) {
                throw new Error('The URL must include both location and user.');
            }

            const result = await apiRequest(buildContextUrl('/api/auth/verify-ejento', browserParams));
            if (!result.success) {
                throw new Error(result.message || 'Authentication failed');
            }

            const authData = result.data || {};
            const resolvedLocation = authData.locationId;
            const resolvedUser = authData.userId;
            if (
                !resolvedLocation
                || !resolvedUser
                || resolvedLocation !== browserParams.location
                || resolvedUser !== browserParams.user
                || !authData.technician
                || !authData.client
            ) {
                throw new Error('The authenticated session is missing its location or user mapping.');
            }

            setTechnician(authData.technician);
            setClient(authData.client);
            setUserRole(authData.userRole || null);

            setUrlParams({
                location: browserParams.location,
                user: browserParams.user,
                integration: browserParams.integration
            });
        } catch (authError) {
            console.error('Auth initialization error:', authError);
            setTechnician(null);
            setClient(null);
            setUserRole(null);
            setError(authError.message || 'Authentication failed');
        } finally {
            setLoading(false);
        }
    };

    const clearLocalSession = () => {
        setTechnician(null);
        setClient(null);
        setUserRole(null);
        setError('');
    };

    const logout = async () => {
        const browserParams = getBrowserAuthParams();
        try {
            await apiRequest(buildContextUrl('/api/auth/logout', browserParams), { method: 'POST' });
        } finally {
            clearLocalSession();

            if (window.parent !== window) {
                window.parent.postMessage({ action: 'logout' }, '*');
            }
        }
    };

    const invalidateSession = () => {
        const browserParams = getBrowserAuthParams();
        clearLocalSession();
        // Clear the HttpOnly cookie through the same trusted-origin endpoint.
        // Navigation must not wait for this request to finish.
        apiRequest(buildContextUrl('/api/auth/logout', browserParams), { method: 'POST' })
            .catch(() => {});
    };

    const refreshAuth = async () => {
        await initializeAuth();
    };

    const updateProfile = async (profileData) => {
        const params = new URLSearchParams();
        if (urlParams.location) params.set('location', urlParams.location);
        if (urlParams.user) params.set('user', urlParams.user);
        if (urlParams.integration) params.set('integration', urlParams.integration);

        const query = params.toString();
        const result = await apiRequest(`/api/auth/profile-ejento${query ? `?${query}` : ''}`, {
            method: 'PUT',
            body: JSON.stringify(profileData)
        });

        if (!result.success) {
            throw new Error(result.message || 'Failed to update profile');
        }

        setTechnician(result.technician);
        return result;
    };

    const isAdmin = () => userRole === 'admin';
    const isManager = () => ['admin', 'manager'].includes(userRole);
    const isTechnician = () => userRole === 'technician';
    const isAuthenticated = () => Boolean(technician);

    const value = {
        technician,
        client,
        userRole,
        loading,
        error,
        urlParams,
        logout,
        invalidateSession,
        refreshAuth,
        updateProfile,
        isAdmin,
        isManager,
        isTechnician,
        isAuthenticated,
        user: technician,
        isEjentoMode: true
    };

    return (
        <UnifiedAuthContext.Provider value={value}>
            {children}
        </UnifiedAuthContext.Provider>
    );
};

export default UnifiedAuthProvider;
